import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import { AppProvider, useApp } from '@/context/AppContext';
import SplashScreen from '@/components/SplashScreen';
import Sidebar from '@/components/Sidebar';
import Dashboard from '@/components/Dashboard';
import EmailList from '@/components/EmailList';
import EmailView from '@/components/EmailView';
import Settings from '@/components/Settings';
import Rubrica from '@/components/Rubrica';
import ComposeModal from '@/components/ComposeModal';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import '@/App.css';

function MainLayout({ children }) {
  return (
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
        {children}
      </div>
      <ComposeModal />
    </div>
  );
}

// Auth guard: redirect to splash if not authenticated
function RequireAuth({ children }) {
  const { isAuthenticated, authChecked } = useApp();
  const location = useLocation();
  if (!authChecked) return null; // waiting for token validation
  if (!isAuthenticated) return <Navigate to="/" state={{ from: location }} replace />;
  return children;
}

function AppContent() {
  const { showChangePassword, authToken, dismissChangePassword } = useApp();

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<SplashScreen />} />
          <Route path="/dashboard" element={
            <RequireAuth><MainLayout><Dashboard /></MainLayout></RequireAuth>
          } />
          <Route path="/settings" element={
            <RequireAuth><MainLayout><Settings /></MainLayout></RequireAuth>
          } />
          <Route path="/rubrica" element={
            <RequireAuth><MainLayout><Rubrica /></MainLayout></RequireAuth>
          } />
          <Route path="/inbox" element={
            <RequireAuth><MainLayout><EmailList folder="inbox" unified={true} /></MainLayout></RequireAuth>
          } />
          <Route path="/folder/:folder" element={
            <RequireAuth><MainLayout><EmailList unified={true} /></MainLayout></RequireAuth>
          } />
          <Route path="/account/:accountId/:folder" element={
            <RequireAuth><MainLayout><EmailList /></MainLayout></RequireAuth>
          } />
          <Route path="/account/:accountId/:folder/:uid" element={
            <RequireAuth><MainLayout><EmailView /></MainLayout></RequireAuth>
          } />
          <Route path="/category/:category" element={
            <RequireAuth><MainLayout><EmailList /></MainLayout></RequireAuth>
          } />
        </Routes>
      </BrowserRouter>

      {showChangePassword && authToken && (
        <ChangePasswordModal token={authToken} onDone={dismissChangePassword} />
      )}

      <Toaster
        position="bottom-right"
        richColors
        theme="light"
        toastOptions={{ style: { fontFamily: 'Manrope, sans-serif' } }}
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme="light">
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
